import React, { useEffect, useState, useRef } from 'react';
import { Spinner, Button, Card, Row, Col, Badge, Nav, ProgressBar } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import useClassiUtenti from '../../hooks/useClassiUtenti';
import useMediaQuery from '../../hooks/useMediaQuery';
import useClassi from '../../hooks/useClassi';
import { TIPO_CORSO } from '../../model/tipoCorso';
import UtilityService from '../../services/UtillityServices/UtilityService';

const ClassiListManager = ({ userId }) => {
  const [classiUtente, setClassiUtente] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const dataFetchedRef = useRef(false);
  const [currentView, setCurrentView] = useState('list'); // 'list' o 'percorso'
  const [percorsoFormativo, setPercorsoFormativo] = useState(null);
  const { getClassiUtenteByUserId } = useClassiUtenti();
  const { getMaterialiClasseByUserId, loadingStateClassi } = useClassi();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const [currentTab, setCurrentTab] = useState('contenuti');

  useEffect(() => {
    if (!dataFetchedRef.current) {
      fetchClassiUtente();
      dataFetchedRef.current = true;
    }
  }, [userId]);

  const fetchClassiUtente = async () => {
    setIsLoading(true);
    try {
      const obj = { id_utente: userId };
      const res = await getClassiUtenteByUserId(obj);
      setClassiUtente(res);
    } catch (error) {
      console.error(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewPercorsoFormativo = async (classe) => {
    setCurrentView('percorso');
    
    const obj = { 
      id_classe: classe.id_classe, 
      id_utente: classe.id_utente, 
      id_classe_utente: classe.id_classe_utente 
    };
    const res = await getMaterialiClasseByUserId(obj);
    setPercorsoFormativo(res);
  };

  const getStatoPagamentoBadge = (stato) => {
    switch(stato) {
      case 'Pagato':
        return 'success';
      case 'Non_pagato':
        return 'danger';
      default:
        return 'primary';
    }
  };

  const getEsitoEsameBadge = (esito) => {
    switch(esito) {
      case 'promosso':
        return { bg: 'success', text: 'light' };
      case 'bocciato':
        return { bg: 'danger', text: 'light' };
      case 'non_svolto':
      default:
        return { bg: 'warning', text: 'dark' };
    }
  };

const renderListaCorsi = () => (
    <div className={`container-fluid ${isMobile ? 'px-2' : 'px-4'}`}>
      {classiUtente?.length > 0 ? (
        <Row className="g-4">
          {classiUtente.map((classe, index) => {

            // Calcoliamo la percentuale di avanzamento
            const oreSeguite = parseFloat(classe.ore_seguite_corso) || 0;
            const oreTotali = parseFloat(classe.ore_totali_corso) || 0;
            const percentage = oreTotali > 0 ? (oreSeguite / oreTotali) * 100 : 0;
            const percentageRounded = Math.min(100, Math.round(percentage));

            return (
              <Col xs={12} key={classe.id_classe_utente}>
                <Card 
                  className="border-0 bg-white" 
                  style={{
                    borderLeft: '4px solid #0d6efd',
                    boxShadow: 'rgba(0, 0, 0, 0.1) 0px 1px 3px 0px, rgba(0, 0, 0, 0.06) 0px 1px 2px 0px'
                  }}
                >
                  <Card.Body className={`${isMobile ? 'p-3' : 'p-4'}`}>
                    <Row className={`align-items-center ${isMobile ? 'g-3' : 'g-3'}`}>
                      {/* Immagine e Titolo (invariato) */}
                      <Col xs={12} lg={4} className={`${!isMobile && 'border-end'}`}>
                        <div className="d-flex align-items-center gap-3">
                          <div 
                            className="rounded-circle flex-shrink-0"
                            style={{
                              width: isMobile ? '40px' : '50px',
                              height: isMobile ? '40px' : '50px',
                              backgroundImage: `url(${classe.corso?.immagine_corso})`,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center'
                            }}
                          />
                          <div>
                            <h6 className={`mb-1 text-dark ${isMobile && 'fs-7'}`}>
                              {classe.corso?.denominazione}{" "} 
                              {classe.corso?.modalita_erogazione !== TIPO_CORSO.TELEMATICA_ASINCRONA && (
                                <span className="text-muted"> ({UtilityService.formatDate(classe.data_inizio)} - {UtilityService.formatDate(classe.data_fine)}) </span>
                              )}
                            </h6>
                            <small className="text-muted d-flex align-items-center gap-2">
                              <i className="bi bi-hash"></i>
                              {classe.corso?.codice}
                            </small>
                          </div>
                        </div>
                      </Col>

                      {/* Info Corso (invariato) */}
                      <Col xs={12} lg={5} className={`${!isMobile && 'border-end'} ${isMobile && 'mt-2'}`}>
                        <Row className={`${isMobile ? 'g-2' : 'g-3'}`}>
                          <Col xs={isMobile ? 12 : 6}>
                            <div className="text-muted">
                              <div className="d-flex align-items-center mb-2">
                                <i className="bi bi-laptop text-primary me-2"></i>
                                <span className="small">{classe.corso?.modalita_erogazione?.replace('_', ' ')}</span>
                              </div>
                              {classe.corso?.modalita_erogazione !== TIPO_CORSO.TELEMATICA_ASINCRONA && (
                                <div className="d-flex align-items-center">
                                  <i className="bi bi-people text-primary me-2"></i>
                                  <span className="small">{classe.denominazione}</span>
                                </div>
                              )}
                            </div>
                          </Col>
                          <Col xs={isMobile ? 12 : 6} className={isMobile ? 'mt-2' : ''}>
                            <div className="text-muted">
                              <div className="d-flex align-items-center mb-2">
                                <i className="bi bi-clock text-primary me-2"></i>
                                <span className="small">{classe.corso?.durata}h totali</span>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                <Badge bg={classe.stato === 'Attivo' ? 'success' : 'danger'} className="rounded-pill"><small>{classe.stato}</small></Badge>
                                <Badge bg={getStatoPagamentoBadge(classe.stato_pagamento)} className="rounded-pill">
                                  <small><i className="bi bi-credit-card me-1"></i>{classe.is_voucher == 1 ? 'Voucher' : classe.stato_pagamento}</small>
                                </Badge>
                              </div>
                            </div>
                          </Col>
                        </Row>
                      </Col>

                      {/* Azioni (invariato) */}
                      <Col xs={12} lg={3} className={`${isMobile ? 'mt-3' : 'text-end'}`}>
                        <div className="d-flex flex-column align-items-end gap-2">
                          <Button variant="primary" size="sm" onClick={() => handleViewPercorsoFormativo(classe)} className={`d-inline-flex align-items-center px-3 ${isMobile && 'w-100 justify-content-center'}`}>
                            <small>Visualizza percorso formativo</small>
                          </Button>
                          {classe.esito_esame && classe.esito_esame !== 'non_svolto' && (
                            <div className={`small text-muted ${isMobile ? 'w-100 text-center' : ''}`}><i className="bi bi-award me-1 text-secondary"></i>Esito esame: <strong>{classe.esito_esame}</strong></div>
                          )}
                          {classe.path_attestato_verticale && (<a href={classe.path_attestato_verticale} className={`btn btn-outline-success btn-sm ${isMobile && 'w-100 text-center'}`} target="_blank" rel="noopener noreferrer"><i className="bi bi-file-earmark-text me-1"></i>Scarica Attestato</a>)}
                          {classe.path_statino && (<a href={classe.path_statino} className={`btn btn-outline-info btn-sm ${isMobile && 'w-100 text-center'}`} target="_blank" rel="noopener noreferrer"><i className="bi bi-file-earmark-arrow-down me-1"></i>Scarica Statino</a>)}
                        </div>
                      </Col>
                      
                      {/* NUOVO BLOCCO PER LA PROGRESS BAR */}
                      <Col xs={12} className="mt-3 pt-3 border-top">
                        <div>
                          <small className="text-muted d-block mb-1">
                            Stato avanzamento: <b>{oreSeguite.toFixed(2)} / {oreTotali.toFixed(2)} ore</b>
                          </small>
                          <ProgressBar
                            now={percentageRounded}
                            label={`${percentageRounded}%`}
                            striped
                            animated
                            variant={percentageRounded >= 100 ? 'success' : 'primary'}
                            style={{ height: '1.2rem', fontSize: '0.8rem' }}
                          />
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>
            )
          })}
        </Row>
      ) : (
        <div className="text-center py-5">
          <div className="bg-light rounded-circle mx-auto mb-3 d-flex align-items-center justify-content-center" style={{ width: isMobile ? '50px' : '60px', height: isMobile ? '50px' : '60px' }}>
            <i className="bi bi-journal-x fs-4 text-muted"></i>
          </div>
          <h6 className="text-muted">Nessun corso assegnato</h6>
        </div>
      )}
    </div>
  );

  const renderPercorsoFormativo = () => {
    const contenutiMap = new Map(percorsoFormativo?.contenuti.map(c => [c.id_contenuto, c]));

    const sortContenuti = (contenuti) => {
      // Funzione per trovare il livello di propedeuticità
      const findLevel = (contenuto) => {
        let level = 0;
        let current = contenuto;
        while (current.id_contenuto_propedeutico) {
          level++;
          current = contenutiMap.get(current.id_contenuto_propedeutico);
          if (!current) break;
        }
        return level;
      };

      return [...contenuti].sort((a, b) => {
        if (a.completato !== b.completato) return a.completato ? -1 : 1;
        
        if (a.completato) {
          return new Date(a.data_fine_prevista) - new Date(b.data_fine_prevista);
        } else {
          const levelA = findLevel(a);
          const levelB = findLevel(b);
          if (levelA !== levelB) return levelA - levelB;
          
          return a.id_contenuto - b.id_contenuto;
        }
      });
    };

    return (
      <div className="container-fluid px-4">
        {/* Header con pulsante indietro */}
        <div className="d-flex align-items-center mb-4">
          <Button 
            variant="link" 
            className="text-dark p-0 me-3"
            onClick={() => {
              setCurrentView('list');
              setCurrentTab('contenuti');
            }}
          >
            <i className="bi bi-arrow-left fs-4"></i>
          </Button>
          <h4 className="mb-0">
            <i className="bi bi-journal-text text-primary me-2"></i>
            Percorso Formativo
          </h4>
        </div>

        {loadingStateClassi.isLoadingMaterialiClasseByUserId ? (
          <div className="text-center py-5">
            <Spinner animation="border" role="status" />
          </div>
        ) : percorsoFormativo && (
          <>
            {/* Info Corso Card */}
            <Card className="border-0 shadow-sm mb-4">
              <Card.Body className="p-4">
                <h4>{percorsoFormativo.denominazione_corso}</h4>
                <p className="text-muted mb-3">{percorsoFormativo.descrizione_corso}</p>
                <div className="d-flex flex-wrap gap-3">
                  {
                    percorsoFormativo.data_inizio && percorsoFormativo.data_fine && (
                      <div className="text-muted">
                        <i className="bi bi-calendar3 me-2"></i>
                        {new Date(percorsoFormativo.data_inizio).toLocaleDateString()} - {new Date(percorsoFormativo.data_fine).toLocaleDateString()}
                      </div>
                    )
                  }
                  {
                    !percorsoFormativo.denominazione?.includes('Classe Online - ') && (
                      <div className="text-muted">
                        <i className="bi bi-people me-2"></i>
                        Classe: {percorsoFormativo.denominazione}
                      </div>
                    )
                  }
                </div>
              </Card.Body>
            </Card>

            {/* Tabs Navigation */}
            <Nav variant="tabs" className="mb-4">
              <Nav.Item>
                <Nav.Link 
                  active={currentTab === 'contenuti'}
                  onClick={() => setCurrentTab('contenuti')}
                >
                  <i className="bi bi-collection-play me-2"></i>
                  Contenuti del corso
                </Nav.Link>
              </Nav.Item>
              {/* <Nav.Item>
                <Nav.Link 
                  active={currentTab === 'calendario'}
                  onClick={() => setCurrentTab('calendario')}
                >
                  <i className="bi bi-calendar-week me-2"></i>
                  Calendario Attività
                  {(percorsoFormativo.lezioni.length > 0 || percorsoFormativo.esami.length > 0) && (
                    <Badge bg="primary" className="ms-2">
                      {percorsoFormativo.lezioni.length + percorsoFormativo.esami.length}
                    </Badge>
                  )}
                </Nav.Link>
              </Nav.Item> */}
            </Nav>

            {/* Tab Content */}
            {currentTab === 'contenuti' ? (
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  {percorsoFormativo.contenuti.length > 0 ? (
                    sortContenuti(percorsoFormativo.contenuti).map((contenuto) => (
                      <div 
                        key={contenuto.id_contenuto} 
                        className="d-flex align-items-center p-3 border-bottom"
                      >
                        <div className="me-3">
                          {contenuto.completato ? (
                            <i className="bi bi-check-circle-fill text-success fs-5"></i>
                          ) : contenuto.id_contenuto_propedeutico && 
                             !contenutiMap.get(contenuto.id_contenuto_propedeutico)?.completato ? (
                            <i className="bi bi-lock-fill text-muted fs-5"></i>
                          ) : (
                            <i className="bi bi-circle text-muted fs-5"></i>
                          )}
                        </div>
                        <div className="flex-grow-1">
                          <div className="fw-medium">{contenuto.titolo}</div>
                          <div className="d-flex align-items-center flex-wrap gap-3 mt-1">
                            <small className="text-muted">
                              <i className="bi bi-folder2 me-1"></i>
                              Area: {contenuto.titolo_area}
                            </small>
                            <small className="text-muted">
                              <i className="bi bi-clock me-1"></i>
                              {contenuto.peso_orario}m
                            </small>
                            {/* {contenuto.completato && contenuto.data_fine_prevista && (
                              <small className="text-success">
                                <i className="bi bi-check-circle me-1"></i>
                                Completato il {new Date(contenuto.data_fine_prevista).toLocaleDateString()}
                              </small>
                            )} */}
                            {contenuto.completato && contenuto.data_fine_prevista && (
                              contenuto.tipo_contenuto === 'test'
                              ?
                                contenuto.id_contenuti_test == null
                                ?
                                  <small className="text-dark">
                                    <i className="bi bi-info-circle me-1 text-primary"></i>
                                    Test non ancora superato
                                  </small>
                                :
                                  <small className="text-success">
                                    <i className="bi bi-check-circle me-1"></i>
                                    Test superato
                                  </small>
                              :
                                <small className="text-success">
                                  <i className="bi bi-check-circle me-1"></i>
                                  Completato il {new Date(contenuto.data_fine_prevista).toLocaleDateString()}
                                </small>
                            )}
                          </div>
                        </div>
                        <Badge bg={contenuto.opzionale ? "secondary" : "primary"} text={contenuto.opzionale ? "dark" : undefined}>
                          {contenuto.opzionale ? "Opzionale" : "Obbligatorio"}
                        </Badge>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <i className="bi bi-journal-x fs-1 text-muted mb-2"></i>
                      <p className="text-muted mb-0">Nessun contenuto disponibile</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            ) : (
              <Card className="border-0 shadow-sm">
                <Card.Body>
                  {percorsoFormativo.lezioni.length > 0 || percorsoFormativo.esami.length > 0 ? (
                    <>
                      {/* Lezioni ed Esami */}
                      <div className="row g-4">
                        {percorsoFormativo.lezioni.map((lezione) => (
                          <div key={lezione.id_lezione} className="col-12 col-lg-6">
                            <Card className="h-100 border-0 shadow-sm">
                              <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                  <div>
                                    <h6 className="mb-1">
                                      <i className="bi bi-camera-video text-primary me-2"></i>
                                      {lezione.titolo}
                                    </h6>
                                    <div className="d-flex align-items-center gap-3">
                                      <small className="text-muted">
                                        <i className="bi bi-calendar3 me-1"></i>
                                        {new Date(lezione.data).toLocaleDateString()}
                                      </small>
                                      <small className="text-muted">
                                        <i className="bi bi-clock me-1"></i>
                                        {lezione.ora_inizio.slice(0, 5)} - {lezione.ora_fine.slice(0, 5)}
                                      </small>
                                    </div>
                                  </div>
                                  <Badge bg="info">Lezione</Badge>
                                </div>

                                {lezione.argomenti?.length > 0 && (
                                  <div className="d-flex gap-2 flex-wrap">
                                    {lezione.argomenti.map(arg => (
                                      <Badge 
                                        key={arg.id_argomento} 
                                        bg="light" 
                                        text="dark" 
                                        className="py-2 px-3"
                                        style={{ fontSize: '0.85em' }}
                                      >
                                        <i className="bi bi-bookmark me-1"></i>
                                        {arg.titolo}
                                      </Badge>
                                    ))}
                                  </div>
                                )}

                                {lezione.note && (
                                  <div className="mt-2 small text-muted">
                                    <i className="bi bi-info-circle me-1"></i>
                                    {lezione.note}
                                  </div>
                                )}
                              </Card.Body>
                            </Card>
                          </div>
                        ))}

                        {percorsoFormativo.esami.map((esame) => (
                          <div key={esame.id_calendario_esame} className="col-12 col-lg-6">
                            <Card className="h-100 border-0 shadow-sm">
                              <Card.Body className="p-3">
                                <div className="d-flex justify-content-between align-items-start mb-3">
                                  <div>
                                    <h6 className="mb-1">
                                      <i className="bi bi-pencil-square text-warning me-2"></i>
                                      Esame
                                    </h6>
                                    <div className="d-flex align-items-center gap-3">
                                      <small className="text-muted">
                                        <i className="bi bi-calendar3 me-1"></i>
                                        {new Date(esame.data).toLocaleDateString()}
                                      </small>
                                      <small className="text-muted">
                                        <i className="bi bi-clock me-1"></i>
                                        {esame.ora_inizio.slice(0, 5)} - {esame.ora_fine.slice(0, 5)}
                                      </small>
                                    </div>
                                  </div>
                                  <Badge bg="warning" text="dark">Esame</Badge>
                                </div>

                                {esame.utenti[0]?.prenotato === 1 && (
                                  <div className="d-flex align-items-center gap-2 mb-2">
                                    <Badge bg="light" text="dark" className="py-2">
                                      <i className="bi bi-calendar-check me-1"></i>
                                      Prenotato il {new Date(esame.utenti[0].data_prenotazione).toLocaleDateString()}
                                    </Badge>
                                  </div>
                                )}

                                {esame.utenti[0]?.esito_esame && (
                                  <div className="d-flex flex-wrap gap-2 align-items-center">
                                    <Badge 
                                      bg={getEsitoEsameBadge(esame.utenti[0].esito_esame).bg}
                                      text={getEsitoEsameBadge(esame.utenti[0].esito_esame).text}
                                      className="py-2"
                                    >
                                      <i className="bi bi-award me-1"></i>
                                      {esame.utenti[0].esito_esame === 'non_svolto' ? 'Non svolto' : 
                                       esame.utenti[0].esito_esame.charAt(0).toUpperCase() + 
                                       esame.utenti[0].esito_esame.slice(1)}
                                      {esame.utenti[0].voto && ` - Voto: ${esame.utenti[0].voto}`}
                                    </Badge>
                                  </div>
                                )}

                                {esame.utenti[0]?.note && (
                                  <div className="mt-2 small text-muted">
                                    <i className="bi bi-info-circle me-1"></i>
                                    {esame.utenti[0].note}
                                  </div>
                                )}

                   {/*              {esame.utenti[0]?.file_statino && (
                                  <div className="mt-3">
                                    <Button 
                                      variant="outline-primary"
                                      size="sm"
                                      href={esame.utenti[0].file_statino}
                                      target="_blank"
                                      className="d-inline-flex align-items-center"
                                    >
                                      <i className="bi bi-download me-2"></i>
                                      Scarica Statino
                                    </Button>
                                  </div>
                                )} */}
                              </Card.Body>
                            </Card>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <div className="text-center py-4">
                      <i className="bi bi-calendar-x fs-1 text-muted mb-2"></i>
                      <p className="text-muted mb-0">Nessuna attività programmata</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            )}
          </>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="text-center py-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Caricamento...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <>
      {currentView === 'list' ? renderListaCorsi() : renderPercorsoFormativo()}
    </>
  );
};

export default ClassiListManager; 